import React from "react";
import { Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { formatValue, measureByKey, valueFor } from "../helpers/turnLevelMeasures";

const RESULT = {
  pass: { label: "Pass", color: "success" },
  hold: { label: "Hold", color: "error" },
  not_evaluable: { label: "Not evaluable", color: "default" },
};

const MATCH = measureByKey("match_rate");
const BRICK = measureByKey("brick_rate");

const Rates = ({ batch }) => (
  <Stack direction="row" spacing={3}>
    {[MATCH, BRICK].map((m) => (
      <Stack key={m.key} spacing={0}>
        <Typography variant="caption" color="text.secondary">
          {m.label}
        </Typography>
        <Typography variant="h6">{batch ? formatValue(m, valueFor(batch, m)) : "—"}</Typography>
      </Stack>
    ))}
  </Stack>
);

const Part = ({ title, batch, children, highlight }) => (
  <Card variant="outlined" sx={{ flex: "1 1 240px", borderColor: highlight ? "primary.main" : undefined }}>
    <CardContent>
      <Stack spacing={1}>
        <Typography variant="subtitle2">{title}</Typography>
        <Rates batch={batch} />
        {children}
      </Stack>
    </CardContent>
  </Card>
);

const gateText = (gate) => (gate ? RESULT[gate.result]?.label || gate.result : "Not available yet (PROG-66951)");
const missText = (gate) =>
  gate?.result === "hold" && gate.missed?.length
    ? `Misses: ${gate.missed.map((k) => measureByKey(k)?.label.toLowerCase() || k).join(", ")}`
    : null;

// First line of the summary: golden set, production, test, and the test
// version's release gate as the API sends it. The UI does not work out the result.
const ReleaseGateLine = ({ goldenSet, production, test }) => {
  const gate = test?.batch.gate;
  const limits = (test?.batch.thresholds || production?.batch.thresholds || []).map((t) => {
    const m = measureByKey(t.eval);
    // A limit for a measure this page does not know still shows, as a plain number.
    const value = m ? formatValue(m, t.value) : String(t.value);
    return `${m?.label.toLowerCase() || t.eval} ${t.direction === "min" ? "at least" : "at most"} ${value}`;
  });
  const placeholder = (test?.batch.thresholds || []).some((t) => t.source === "placeholder");

  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Part title="Golden set" batch={goldenSet}>
          <Typography variant="caption" color="text.secondary">
            What the golden set expects
          </Typography>
        </Part>
        {production && (
          <Part title={`Production: ${production.label}`} batch={production.batch}>
            <Typography variant="caption" color="text.secondary">
              Release gate: {gateText(production.batch.gate)}
            </Typography>
          </Part>
        )}
        {test && (
          <Part title={`Test: ${test.label}`} batch={test.batch} highlight>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                label={`Release gate: ${gateText(gate)}`}
                color={gate ? RESULT[gate.result]?.color : "default"}
                variant={gate ? "filled" : "outlined"}
                size="small"
              />
              {missText(gate) && (
                <Typography variant="body2" color="error">
                  {missText(gate)}
                </Typography>
              )}
            </Stack>
          </Part>
        )}
      </Stack>
      {limits.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Limits: {limits.join(", ")}.{placeholder && " Placeholder until a baseline run."}
        </Typography>
      )}
    </Stack>
  );
};

export default ReleaseGateLine;
